import { useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MDLINK_LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff } from "lucide-react";

export default function Login() {
    const { user, login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPwd, setShowPwd] = useState(false);
    const [loading, setLoading] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (res.ok) {
            toast.success("Sesión iniciada");
            navigate("/");
        } else {
            toast.error(res.error || "No se pudo iniciar sesión");
        }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] bg-white">
            {/* Left: form */}
            <div className="flex items-center justify-center px-6 py-10 lg:px-14">
                <div className="w-full max-w-md">
                    <Link to="/login" className="flex items-center gap-4 mb-10" data-testid="login-logo">
                        <img
                            src={MDLINK_LOGO}
                            alt="MD Link"
                            className="h-12 w-auto"
                            draggable={false}
                        />
                        <div className="pl-4 border-l border-slate-200 leading-tight">
                            <div className="font-heading font-semibold text-[15px] tracking-tight text-slate-900">
                                Cotizador
                            </div>
                            <div className="text-[10.5px] font-mono uppercase tracking-widest text-slate-500 mt-0.5">
                                Plataforma B2B · CR
                            </div>
                        </div>
                    </Link>

                    <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900">
                        Bienvenido de vuelta.
                    </h1>
                    <p className="mt-3 text-slate-600 leading-relaxed">
                        Ingresá para armar cotizaciones de anaqueles y equipos
                        Opus Pac de unidosis en minutos.
                    </p>

                    <form className="mt-8 space-y-5" onSubmit={onSubmit} data-testid="login-form">
                        <div className="space-y-1.5">
                            <Label htmlFor="login-email" className="text-xs font-mono uppercase tracking-widest text-slate-500">
                                Correo
                            </Label>
                            <Input
                                id="login-email"
                                data-testid="login-email-input"
                                type="email"
                                autoComplete="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tu@empresa.com"
                                className="h-11 rounded-md border-slate-200 focus-visible:ring-[#1BA5D9]"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <div className="flex items-center justify-between">
                                <Label htmlFor="login-password" className="text-xs font-mono uppercase tracking-widest text-slate-500">
                                    Contraseña
                                </Label>
                            </div>
                            <div className="relative">
                                <Input
                                    id="login-password"
                                    data-testid="login-password-input"
                                    type={showPwd ? "text" : "password"}
                                    autoComplete="current-password"
                                    required
                                    minLength={6}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                    className="h-11 rounded-md border-slate-200 focus-visible:ring-[#1BA5D9] pr-10"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPwd((v) => !v)}
                                    className="absolute inset-y-0 right-2 grid place-items-center text-slate-400 hover:text-slate-700"
                                    aria-label="Mostrar contraseña"
                                    data-testid="login-toggle-visibility"
                                >
                                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            data-testid="login-submit-button"
                            type="submit"
                            disabled={loading}
                            className="h-11 w-full rounded-md bg-[#1BA5D9] hover:bg-[#0E7DAB] active:scale-[.99] transition-all duration-150 font-medium"
                        >
                            {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Iniciar sesión
                        </Button>

                        <div className="text-sm text-slate-600 text-center">
                            ¿No tenés cuenta?{" "}
                            <Link data-testid="login-goto-register" to="/register" className="text-[#1BA5D9] font-medium hover:underline">
                                Crear cuenta
                            </Link>
                        </div>
                    </form>

                    <div className="mt-10 text-[11px] font-mono uppercase tracking-widest text-slate-400 flex items-center gap-3">
                        <span className="h-px flex-1 bg-slate-200" />
                        MD Link · mdlink.tech
                        <span className="h-px flex-1 bg-slate-200" />
                    </div>
                </div>
            </div>

            {/* Right: image */}
            <div className="hidden lg:block relative">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{
                        backgroundImage:
                            "url(https://images.unsplash.com/photo-1576085898384-b3cdb88736e9?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80)",
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-br from-slate-900/70 via-[#1BA5D9]/50 to-slate-900/60" />
                <div className="absolute inset-0 bg-grid opacity-10 mix-blend-overlay" />
                <div className="relative h-full flex flex-col justify-end p-14 text-white">
                    <div className="max-w-md">
                        <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-white/70 mb-4">
                            Distribuidor autorizado Opus Pac · Costa Rica
                        </div>
                        <h2 className="font-heading text-3xl xl:text-4xl font-semibold tracking-tight leading-tight">
                            Cotizaciones profesionales para hospitales y clínicas, sin hojas de cálculo dispersas.
                        </h2>
                        <div className="mt-6 grid grid-cols-3 gap-4 text-sm">
                            <Stat label="Modos de venta" value="04" />
                            <Stat label="Familias" value="04" />
                            <Stat label="Referencias" value="35+" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return (
        <div className="border-l-2 border-white/40 pl-3">
            <div className="font-mono text-2xl font-semibold leading-none">{value}</div>
            <div className="text-[11px] uppercase tracking-widest text-white/70 mt-1">{label}</div>
        </div>
    );
}
